<script setup lang="ts">
import PanelGroup from '../PanelGroup.vue'
import InputCaracterCounter from '@/components/InputCaracterCounter.vue'
import type { LoyaltyProgram } from '@/types/types'
import ConsentWordingEditor from '../ConsentWordingEditor.vue'
import { renderWordingWithLabels } from '../consentWording'
import { isInvalidUrlInput, safeHttpUrl } from '../../lib/brandAppUrl'

const { user } = useLocalStorage()

const { t } = useI18n()

const CGU_WORDING_MAX_LENGTH = 300

const props = defineProps<{
  loyaltyProgram: LoyaltyProgram
}>()

const emit = defineEmits<{
  (e: 'update:loyaltyProgram', value: LoyaltyProgram): void
}>()

const updateLoyaltyProgram = (field: 'cguUrl' | 'cguWording' | 'privacyPolicyUrl', value: string | number) => {
  emit('update:loyaltyProgram', {
    ...props.loyaltyProgram,
    [field]: String(value)
  })
}

type WordingSegment = {
  type: 'text' | 'cgu' | 'privacyPolicy'
  value: string
}

const hasCguUrl = computed(() => !!props.loyaltyProgram.cguUrl?.trim())
const hasPrivacyPolicyUrl = computed(() => !!props.loyaltyProgram.privacyPolicyUrl?.trim())

const isDefaultWordingPreview = computed(
  () => !props.loyaltyProgram.cguWording?.trim() && (hasCguUrl.value || hasPrivacyPolicyUrl.value)
)

const defaultWordingKey = computed(() => {
  if (hasCguUrl.value && hasPrivacyPolicyUrl.value) return 'pages.loyaltySupports.webapp.config.cguWordingDefault'
  if (hasCguUrl.value) return 'pages.loyaltySupports.webapp.config.cguWordingDefaultCguOnly'
  return 'pages.loyaltySupports.webapp.config.cguWordingDefaultPrivacyOnly'
})

const defaultWording = computed(() => {
  if (!hasCguUrl.value && !hasPrivacyPolicyUrl.value) return ''
  return t(defaultWordingKey.value, { merchantName: user.value.merchant.name })
})

const previewWording = computed(() => props.loyaltyProgram.cguWording?.trim() || defaultWording.value)

const renderedWording = computed(() =>
  renderWordingWithLabels(previewWording.value, {
    cgu: t('pages.loyaltySupports.webapp.config.cguLinkLabel'),
    privacyPolicy: t('pages.loyaltySupports.webapp.config.privacyPolicyLinkLabel')
  })
)

const wordingPreviewSegments = computed<WordingSegment[]>(() => {
  const wording = previewWording.value
  if (!wording) return []

  const segments: WordingSegment[] = []
  const pattern = /\{(cgu|privacyPolicy)(?::([^}]+))?\}/g
  let lastIndex = 0
  let match = pattern.exec(wording)

  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: wording.slice(lastIndex, match.index) })
    }
    segments.push({ type: match[1] as WordingSegment['type'], value: match[2]?.trim() ?? '' })
    lastIndex = pattern.lastIndex
    match = pattern.exec(wording)
  }
  if (lastIndex < wording.length) {
    segments.push({ type: 'text', value: wording.slice(lastIndex) })
  }

  return segments
})
</script>

<template>
  <PanelGroup>
    <div class="flex flex-col gap-5">
      <AppFormInput
        id="cguUrl"
        :model-value="loyaltyProgram.cguUrl"
        type="url"
        :errors="
          isInvalidUrlInput(loyaltyProgram.cguUrl)
            ? [t('pages.loyaltySupports.webapp.editor.validation.invalidUrl')]
            : []
        "
        :placeholder="t('pages.loyaltySupports.webapp.editor.settings.cguPlaceholder')"
        :label="t('pages.loyaltySupports.webapp.config.cguUrl')"
        @update:model-value="updateLoyaltyProgram('cguUrl', $event)"
      />
      <AppFormInput
        id="privacyPolicyUrl"
        :model-value="loyaltyProgram.privacyPolicyUrl"
        type="url"
        :errors="
          isInvalidUrlInput(loyaltyProgram.privacyPolicyUrl)
            ? [t('pages.loyaltySupports.webapp.editor.validation.invalidUrl')]
            : []
        "
        :placeholder="t('pages.loyaltySupports.webapp.editor.settings.privacyPlaceholder')"
        :label="t('pages.loyaltySupports.webapp.config.privacyPolicyUrl')"
        @update:model-value="updateLoyaltyProgram('privacyPolicyUrl', $event)"
      />
      <div class="flex flex-col gap-1.5">
        <label for="cguWording" class="text-sm font-normal text-foreground">
          {{ t('pages.loyaltySupports.webapp.config.cguWording') }}
        </label>
        <ConsentWordingEditor
          id="cguWording"
          :model-value="loyaltyProgram.cguWording"
          :max-length="CGU_WORDING_MAX_LENGTH"
          :has-cgu-url="hasCguUrl"
          :has-privacy-policy-url="hasPrivacyPolicyUrl"
          :default-wording="defaultWording"
          @update:model-value="updateLoyaltyProgram('cguWording', $event)"
        />
        <InputCaracterCounter :content="renderedWording" :max-length="CGU_WORDING_MAX_LENGTH" />
        <div v-if="wordingPreviewSegments.length" class="mt-2 flex flex-col gap-1.5">
          <p class="text-sm font-normal text-foreground">
            {{ t('pages.loyaltySupports.webapp.config.cguWordingPreview') }}
            <span v-if="isDefaultWordingPreview" class="text-muted-foreground">
              {{ t('pages.loyaltySupports.webapp.config.cguWordingDefaultTag') }}
            </span>
          </p>
          <p class="rounded-md border border-border bg-muted p-3 text-sm font-normal text-foreground-secondary">
            <template v-for="(segment, index) in wordingPreviewSegments" :key="index">
              <template v-if="segment.type === 'text'">{{ segment.value }}</template>
              <a
                v-else-if="segment.type === 'cgu'"
                :href="safeHttpUrl(loyaltyProgram.cguUrl) || undefined"
                target="_blank"
                class="underline"
                >{{ segment.value || t('pages.loyaltySupports.webapp.config.cguLinkLabel') }}</a
              >
              <a
                v-else
                :href="safeHttpUrl(loyaltyProgram.privacyPolicyUrl) || undefined"
                target="_blank"
                class="underline"
                >{{ segment.value || t('pages.loyaltySupports.webapp.config.privacyPolicyLinkLabel') }}</a
              >
            </template>
          </p>
        </div>
      </div>
    </div>
  </PanelGroup>
</template>
